import { loadsIntoPayout } from './subs.js'

/**
 * What goes into a new payout.
 *
 * There is no list of defaults to maintain. A new payout copies the last one,
 * so the list he edits *is* the list that repeats: delete a line this month
 * and it does not come back, add one and it carries. The only time the seeded
 * starter list is used is the very first payout, when there is nothing to copy.
 *
 * Two kinds of line are never copied, because they arrive on their own
 * schedule and copying them would double them up:
 *
 *   - an installment charge, which reloads from its plan
 *   - a subscription, which reloads by its own cadence — copying a yearly one
 *     would make it reappear the month after it billed
 *
 * So a new payout is: last month's ordinary lines, plus the subscriptions due
 * this month, plus this month's installment charges. Pure, so the awkward
 * cases can be tested without a database.
 */

/** The fields a copied line carries. Status and sort order are set fresh. */
function carry(p) {
  return {
    name: p.name,
    amount: p.amount,
    category_id: p.category_id ?? null,
    build_id: p.build_id ?? null,
    to_account_id: p.to_account_id ?? null,
    from_default_id: p.from_default_id ?? null,
  }
}

/**
 * @param prevPayables  the previous payout's lines, or [] if there is none
 * @param chargedIds    ids of those lines that are installment charges
 * @param defaults      payable_defaults rows — the starter list and the
 *                      subscription records, which are the same table
 * @param monthKey      "2026-09"
 * @param n             which payout of this month this is, 1-based
 */
export function planPayout({ prevPayables = [], chargedIds = [], defaults = [], monthKey, n = 1 }) {
  const charged = new Set(chargedIds)
  const subIds = new Set(defaults.filter((d) => d.is_subscription).map((d) => d.id))

  /* No previous payout at all: the seeded list is the starting point, once.
     After this it lives on by being copied, and he can delete his way out of
     it like any other line. */
  if (!prevPayables.length) {
    return defaults
      .filter((d) => loadsIntoPayout(d, monthKey, n))
      .map((d) => ({
        name: d.name,
        amount: d.amount,
        category_id: d.category_id ?? null,
        build_id: d.build_id ?? null,
        to_account_id: d.to_account_id ?? null,
        /* Every seeded line remembers its record, subscription or not. If he
           marks one a subscription later, the copy below knows to stand aside
           for the cadence instead of producing the line twice. */
        from_default_id: d.id,
      }))
  }

  const byId = Object.fromEntries(defaults.map((d) => [d.id, d]))
  const copied = prevPayables
    .filter((p) => !charged.has(p.id))
    /* A subscription line carries across in its usual place when it is due
       again, and drops out of the months it does not bill. Keeping it in
       place is why the order of his list does not shuffle every month. */
    .filter((p) => {
      if (!p.from_default_id || !subIds.has(p.from_default_id)) return true
      return loadsIntoPayout(byId[p.from_default_id], monthKey, n)
    })
    .map(carry)

  /* Subscriptions come from their own records so the cadence decides, not
     what last month happened to contain. Anything already carried across with
     that record's id is left alone — matched on the id, never on the name. */
  const present = new Set(copied.map((p) => p.from_default_id).filter(Boolean))
  const subs = defaults
    .filter((d) => d.is_subscription && !present.has(d.id) && loadsIntoPayout(d, monthKey, n))
    .map((d) => ({
      name: d.name,
      amount: d.amount,
      category_id: d.category_id ?? null,
      build_id: d.build_id ?? null,
      to_account_id: d.to_account_id ?? null,
      from_default_id: d.id,
    }))

  return [...copied, ...subs]
}

/**
 * The payout to copy from: the latest one before this payout, looking back
 * through earlier months if this month has none yet. Payouts arrive sorted by
 * month and then by sort_order, oldest first.
 */
export function previousPayout(allPayouts, newPayoutId) {
  const earlier = allPayouts
    .filter((p) => p.id !== newPayoutId)
    .sort((a, b) =>
      String(a.month_key).localeCompare(String(b.month_key)) ||
      (a.sort_order ?? 0) - (b.sort_order ?? 0),
    )
  return earlier[earlier.length - 1] ?? null
}
